import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { loginStart } from '../lib/api';

/**
 * /login?session=<oauth_session_id>
 *
 * Hit by:
 *   1. MCP clients (ChatGPT, Claude) — they get redirected here from
 *      auth.thedraftlion.com/oauth/authorize with a `session` param.
 *   2. Our own frontend — same flow, just for /manage.
 *
 * The page collects the user's email and POSTs to /oauth/login/start, which
 * fires a magic-link email. We tell the user "check your email" regardless
 * of whether the email matched a real subscription, to avoid leaking
 * account-existence info.
 */
export default function Login() {
  const [params] = useSearchParams();
  const session = params.get('session') ?? '';

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Surface a clear error if someone navigates here without a session
  // (i.e. without going through /authorize first). Prevents a confusing
  // 400 after they've typed their email.
  useEffect(() => {
    if (!session) setError('Missing session. Start sign-in from the Manage or Setup page.');
  }, [session]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!session) return;
    setError(null);
    setLoading(true);
    try {
      await loginStart(email.trim(), session);
      setSent(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  if (sent) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <div className="text-5xl mb-6">📬</div>
        <h1 className="text-3xl md:text-4xl font-bold">Check your email</h1>
        <p className="mt-6 text-slate-300 text-lg leading-relaxed">
          If <strong className="text-white">{email}</strong> matches an active DraftLion
          subscription, we just sent you a sign-in link. It expires in 15 minutes.
        </p>
        <p className="mt-4 text-sm text-slate-400">
          Don't see it? Check your spam folder. Still nothing? Email{' '}
          <a className="text-amber-400 hover:text-amber-300" href="mailto:support@thedraftlion.com">
            support@thedraftlion.com
          </a>.
        </p>
        <button
          onClick={() => { setSent(false); setEmail(''); }}
          className="mt-10 text-sm text-slate-400 hover:text-white underline"
        >
          Use a different email
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-6 py-24">
      <h1 className="text-3xl md:text-4xl font-bold">Sign in to DraftLion</h1>
      <p className="mt-4 text-slate-300">
        Enter the email you used at checkout. We'll send you a one-click sign-in link —
        no passwords to remember.
      </p>

      <form onSubmit={handleSubmit} className="mt-8 space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
            Email address
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            required
            disabled={!session}
            className="w-full px-4 py-3 rounded-lg
                       bg-slate-900 border border-slate-700
                       text-slate-100 placeholder-slate-600
                       focus:outline-none focus:border-amber-500
                       disabled:opacity-50"
          />
        </div>

        <button
          type="submit"
          disabled={loading || !email.trim() || !session}
          className="btn-primary w-full"
        >
          {loading ? 'Sending link…' : 'Email me a sign-in link'}
        </button>

        {error && (
          <p className="text-sm text-red-400 mt-3">{error}</p>
        )}
      </form>

      <div className="mt-12 rounded-lg border border-slate-800 bg-slate-900/40 p-4 text-sm text-slate-400">
        <strong className="text-slate-300">First time here?</strong> You need an active
        DraftLion subscription (or free trial) to sign in.{' '}
        <a className="text-amber-400 hover:text-amber-300" href="/">Start a 3-day free trial</a>{' '}
        — then $3.99/month or $22.99/year.
      </div>
    </div>
  );
}
